export interface SpreadPosition {
  id: string;
  name: string;
  description: string;
  x: number;
  y: number;
  /** Rotação em graus (usado pela carta que cruza no centro da Cruz Celta). */
  rotate?: number;
}

export interface SpreadType {
  id: string;
  name: string;
  description: string;
  cardCount: number;
  positions: SpreadPosition[];
  layout: 'line' | 'cross' | 'circle' | 'custom';
  category: 'general' | 'love' | 'daily' | 'decision';
  /** Proporção largura/altura do "tabuleiro" onde as posições são plotadas
   * (extraída dos diagramas oficiais de cada tiragem — ver Downloads/Diagrama
   * das tiragens). Usada pra dimensionar o tabuleiro mantendo o formato certo. */
  boardAspect: number;
  /** Largura de cada carta como % da largura do tabuleiro — controla o quão
   * "apertadas" as cartas ficam lado a lado nessa tiragem específica. */
  cardScale: number;
  /** Baralho de origem da tiragem. Ausente/'tarot' = Tarot Rider-Waite
   * (comportamento padrão, sem mudança pras tiragens já existentes).
   * 'cigano' = Baralho Cigano/Lenormand; 'sibilla' = Baralho Sibila/Vera
   * Sibilla Italiana — cada um muda o pool de cartas usado em
   * ConsultationPage e o endpoint de backend chamado. */
  deck?: 'tarot' | 'cigano' | 'sibilla';
}

export const spreadTypes: SpreadType[] = [
  {
    id: 'cruz-celta',
    name: 'Cruz Celta',
    description: 'Uma das tiragens mais tradicionais e completas, ideal para análises profundas de uma situação.',
    cardCount: 10,
    layout: 'custom',
    category: 'general',
    boardAspect: 0.667,
    cardScale: 13,
    positions: [
      { id: '1', name: 'Situação Atual', description: 'Energia central do consulente', x: 37, y: 40 },
      { id: '2', name: 'Obstáculos', description: 'Desafios que bloqueiam o avanço', x: 37, y: 54, rotate: 90 },
      { id: '3', name: 'Sombra', description: 'Comportamentos internos ou energias externas contrárias', x: 37, y: 70 },
      { id: '4', name: 'Energia Feminina', description: 'Acolhimento, emoção, passividade', x: 12, y: 50 },
      { id: '5', name: 'Eu Superior', description: 'Consciência espiritual, potencial', x: 37, y: 22 },
      { id: '6', name: 'Energia Masculina', description: 'Racionalidade, ação, determinação', x: 62, y: 50 },
      { id: '7', name: 'Máscara Social', description: 'Como o consulente se mostra ao mundo', x: 89, y: 78 },
      { id: '8', name: 'Ambiente', description: 'Influências externas, favoráveis ou não', x: 89, y: 58 },
      { id: '9', name: 'Desejos e Receios', description: 'Motivações ocultas, medos, aspirações', x: 89, y: 39 },
      { id: '10', name: 'Resultado Provável', description: 'Síntese final a partir das demais cartas', x: 89, y: 21 }
    ]
  },
  {
    id: 'metodo-peladan',
    name: 'Método Peladan',
    description: 'Clássico e objetivo, ótimo para uma visão prática de uma situação.',
    cardCount: 5,
    layout: 'cross',
    category: 'general',
    boardAspect: 0.9,
    cardScale: 20,
    positions: [
      { id: '1', name: 'Casa Positiva', description: 'Forças favoráveis', x: 19, y: 50 },
      { id: '2', name: 'Casa Negativa', description: 'Obstáculos e posturas equivocadas', x: 81, y: 50 },
      { id: '3', name: 'Futuro a Longo Prazo', description: 'Desdobramentos distantes', x: 50, y: 20 },
      { id: '4', name: 'Futuro Próximo', description: 'Energia imediata (alerta se negativa)', x: 50, y: 79 },
      { id: '5', name: 'Casa-Chave', description: 'Resultado provável ou visão do consulente', x: 50, y: 50 }
    ]
  },
  {
    id: 'templo-afrodite',
    name: 'Templo de Afrodite',
    description: 'Especial para relacionamentos e vínculos amorosos.',
    cardCount: 7,
    layout: 'custom',
    category: 'love',
    boardAspect: 0.667,
    cardScale: 18,
    positions: [
      { id: '1', name: 'Pensamentos do Parceiro', description: 'Visão do parceiro sobre você', x: 23, y: 15 },
      { id: '2', name: 'Sentimentos do Parceiro', description: 'Sentimentos do parceiro', x: 23, y: 38 },
      { id: '3', name: 'Atração do Parceiro', description: 'Química e atração do parceiro', x: 23, y: 62 },
      { id: '4', name: 'Seus Pensamentos', description: 'Sua visão sobre o parceiro', x: 78, y: 15 },
      { id: '5', name: 'Seus Sentimentos', description: 'Seus sentimentos', x: 78, y: 38 },
      { id: '6', name: 'Sua Atração', description: 'Sua química e atração', x: 78, y: 62 },
      { id: '7', name: 'Tendência Futura', description: 'Tendência futura da relação', x: 50, y: 87 }
    ]
  },
  {
    id: 'pequeno-mestre',
    name: 'Pequeno Mestre',
    description: 'Análise direta dos quatro planos da vida + conselho final.',
    cardCount: 5,
    layout: 'custom',
    category: 'general',
    boardAspect: 0.85,
    cardScale: 20,
    positions: [
      { id: '1', name: 'Plano Material', description: 'Trabalho, saúde, lar, finanças', x: 19, y: 46 },
      { id: '2', name: 'Plano Mental', description: 'Ideias, medos, saúde mental', x: 26, y: 79 },
      { id: '3', name: 'Plano Emocional', description: 'Autoestima, amor, sentimentos', x: 73, y: 79 },
      { id: '4', name: 'Plano Espiritual', description: 'Energia, conexões, influências', x: 81, y: 46 },
      { id: '5', name: 'Conselho Final', description: 'Conselho geral de direcionamento', x: 50, y: 18 }
    ]
  },
  {
    id: 'tres-cartas-tempo',
    name: 'Três Cartas - Passado, Presente e Futuro',
    description: 'Simples e eficaz para perguntas diretas.',
    cardCount: 3,
    layout: 'line',
    category: 'general',
    boardAspect: 1.5,
    cardScale: 20,
    positions: [
      { id: '1', name: 'Passado', description: 'Origem da situação', x: 19, y: 50 },
      { id: '2', name: 'Presente', description: 'Energia atual e principal desafio', x: 50, y: 50 },
      { id: '3', name: 'Futuro', description: 'Possível direção da questão', x: 81, y: 50 }
    ]
  },
  {
    id: 'tres-cartas-decisao',
    name: 'Três Cartas - Situação, Orientação e Resultado',
    description: 'Excelente para decisões práticas.',
    cardCount: 3,
    layout: 'line',
    category: 'decision',
    boardAspect: 1.5,
    cardScale: 20,
    positions: [
      { id: '1', name: 'Situação', description: 'Estado atual', x: 19, y: 50 },
      { id: '2', name: 'Orientação', description: 'Conselho ou atitude a tomar', x: 50, y: 50 },
      { id: '3', name: 'Resultado', description: 'Desfecho provável ao seguir o conselho', x: 81, y: 50 }
    ]
  },
  {
    id: 'sim-nao',
    name: 'Sim ou Não',
    description: 'Método direto para respostas objetivas.',
    cardCount: 2,
    layout: 'line',
    category: 'decision',
    boardAspect: 1.5,
    cardScale: 26,
    positions: [
      { id: '1', name: 'Casa Positiva', description: 'Se positiva, tende ao "sim"', x: 27, y: 50 },
      { id: '2', name: 'Casa Negativa', description: 'Se negativa, tende ao "não"', x: 73, y: 50 }
    ]
  },
  {
    id: 'tarot-dia',
    name: 'Tarot do Dia',
    description: 'Uso diário para orientação e previsões rápidas.',
    cardCount: 1,
    layout: 'line',
    category: 'daily',
    boardAspect: 1,
    cardScale: 38,
    positions: [
      { id: '1', name: 'Energia do Dia', description: 'Energia central do dia, potencialidades e alertas', x: 50, y: 50 }
    ]
  },
  {
    id: 'mandala-astrologica',
    name: 'Mandala Astrológica',
    description: 'Uma das tiragens mais completas, abrange todas as áreas da vida.',
    cardCount: 13,
    layout: 'circle',
    category: 'general',
    boardAspect: 1,
    cardScale: 10,
    positions: [
      { id: '1', name: 'Casa 1 - Áries', description: 'Personalidade e aparência', x: 10, y: 49 },
      { id: '2', name: 'Casa 2 - Touro', description: 'Finanças e bens', x: 17, y: 65 },
      { id: '3', name: 'Casa 3 - Gêmeos', description: 'Comunicação, irmãos, estudos rápidos', x: 31, y: 79 },
      { id: '4', name: 'Casa 4 - Câncer', description: 'Lar, mãe, passado', x: 50, y: 90 },
      { id: '5', name: 'Casa 5 - Leão', description: 'Ego, filhos, criatividade', x: 69, y: 79 },
      { id: '6', name: 'Casa 6 - Virgem', description: 'Trabalho, rotina, saúde', x: 83, y: 65 },
      { id: '7', name: 'Casa 7 - Libra', description: 'Parcerias, amor, justiça', x: 90, y: 49 },
      { id: '8', name: 'Casa 8 - Escorpião', description: 'Segredos, sexualidade, transformações', x: 83, y: 32 },
      { id: '9', name: 'Casa 9 - Sagitário', description: 'Filosofia, espiritualidade, estudos longos', x: 69, y: 18 },
      { id: '10', name: 'Casa 10 - Capricórnio', description: 'Carreira, pai, autoridades', x: 50, y: 10 },
      { id: '11', name: 'Casa 11 - Aquário', description: 'Futuro, projetos, causas coletivas', x: 31, y: 18 },
      { id: '12', name: 'Casa 12 - Peixes', description: 'Inconsciente, espiritualidade, sacrifícios', x: 17, y: 32 },
      { id: '13', name: 'Força Oculta', description: 'Apoio central à leitura', x: 50, y: 49 }
    ]
  }
];

// Tiragens do Baralho Cigano (Lenormand) — catálogo separado do Tarot acima,
// mesmo padrão de boardAspect/cardScale/positions pra reusar CardSelectionGrid.
export const ciganoSpreadTypes: SpreadType[] = [
  {
    id: 'cigano-carta-unica',
    name: 'Carta Única',
    description: 'Resposta rápida e direta pra uma pergunta objetiva.',
    cardCount: 1,
    layout: 'line',
    category: 'daily',
    deck: 'cigano',
    boardAspect: 1,
    cardScale: 38,
    positions: [
      { id: '1', name: 'Resposta', description: 'A energia central e direta da questão', x: 50, y: 50 }
    ]
  },
  {
    id: 'cigano-tres-cartas',
    name: 'Situação, Ação e Resultado',
    description: 'As três cartas se combinam numa frase só — leitura direta do que está acontecendo, o que fazer e onde isso leva.',
    cardCount: 3,
    layout: 'line',
    category: 'general',
    deck: 'cigano',
    boardAspect: 1.5,
    cardScale: 20,
    positions: [
      { id: '1', name: 'Situação', description: 'O que está de fato acontecendo agora', x: 19, y: 50 },
      { id: '2', name: 'Ação', description: 'O que fazer a respeito, sem rodeios', x: 50, y: 50 },
      { id: '3', name: 'Resultado', description: 'Pra onde isso tende a ir', x: 81, y: 50 }
    ]
  }
];

// Tiragens do Baralho Sibila (Vera Sibilla Italiana) — catálogo separado,
// mesmo padrão dos outros dois. "Sim ou Não" é a tiragem mais tradicional
// da Sibila: duas cartas lidas sempre em par.
export const sibillaSpreadTypes: SpreadType[] = [
  {
    id: 'sibilla-carta-unica',
    name: 'Carta Única',
    description: 'Resposta rápida e direta pra uma pergunta objetiva.',
    cardCount: 1,
    layout: 'line',
    category: 'daily',
    deck: 'sibilla',
    boardAspect: 1,
    cardScale: 38,
    positions: [
      { id: '1', name: 'Resposta', description: 'A energia central e direta da questão', x: 50, y: 50 }
    ]
  },
  {
    id: 'sibilla-sim-nao',
    name: 'Sim ou Não',
    description: 'A tiragem mais clássica da Sibila — duas cartas lidas juntas, em par, pra uma resposta objetiva.',
    cardCount: 2,
    layout: 'line',
    category: 'decision',
    deck: 'sibilla',
    boardAspect: 1.5,
    cardScale: 26,
    positions: [
      { id: '1', name: 'Primeira carta', description: 'Define o tom inicial da resposta', x: 27, y: 50 },
      { id: '2', name: 'Segunda carta', description: 'Confirma, contradiz ou modifica a primeira', x: 73, y: 50 }
    ]
  },
  {
    id: 'sibilla-tres-cartas',
    name: 'Situação, Ação e Resultado',
    description: 'As três cartas se combinam numa leitura só — o que está acontecendo, o que fazer e onde isso leva.',
    cardCount: 3,
    layout: 'line',
    category: 'general',
    deck: 'sibilla',
    boardAspect: 1.5,
    cardScale: 20,
    positions: [
      { id: '1', name: 'Situação', description: 'O que está de fato acontecendo agora', x: 19, y: 50 },
      { id: '2', name: 'Ação', description: 'O que fazer a respeito, sem rodeios', x: 50, y: 50 },
      { id: '3', name: 'Resultado', description: 'Pra onde isso tende a ir', x: 81, y: 50 }
    ]
  }
];

export const getSpreadById = (id: string): SpreadType | undefined => {
  return spreadTypes.find(spread => spread.id === id)
    || ciganoSpreadTypes.find(spread => spread.id === id)
    || sibillaSpreadTypes.find(spread => spread.id === id);
};

export const getSpreadsByCategory = (category: string): SpreadType[] => {
  return spreadTypes.filter(spread => spread.category === category);
};
